import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../environments/environment';

export enum CommandType {
  get_controller = 'get_controller',
  get_inverter = 'get_inverter',
  get_energy_meter = 'get_energy_meter',
  get_ems = 'get_ems',
  get_bms = 'get_bms',
  get_io = 'get_io',
  get_energy_meter_log = 'get_energy_meter_log',
  get_ext_io_normal = 'get_ext_io_normal',
  get_ext_io_gen2 = 'get_ext_io_gen2',
  get_ext_io_analog = 'get_ext_io_analog',
  get_ext_io = 'get_ext_io', // TODO: In the future this should be implemented instead of the three above.
}

export interface CommandData {
  boxId: number;
}

export interface CommandLogData {
  assetId: number;
  lastLogId: number;
}

@Injectable({
  providedIn: 'root',
})
export class SocketService implements OnDestroy {
  private socket!: Socket;
  private reconnectAttempts = 0;
  private readonly maxReconnectDelay = 30000; // 30s cap
  private readonly baseReconnectDelay = 1000; // 1s starting delay
  private connectionStatus$ = new Subject<boolean>();

  constructor() {
    this.connect();
  }

  private connect(): void {
    this.socket = io(`${environment.socketUrl}:${environment.socketPort}`, {
      transports: ['websocket'],
      reconnection: false,
      secure: true,
    });

    this.socket.on('connect', () => {
      this.reconnectAttempts = 0;
      this.connectionStatus$.next(true);
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('Reconnected to server:', attemptNumber);
    });

    this.socket.on('reconnect_error', (error) => {
      console.error('Reconnection failed:', error);
      this.connectionStatus$.next(false);
      this.scheduleReconnect();
    });

    this.socket.on('connect_error', (err) => {
      console.error(' Socket connect error:', err.message);
      this.connectionStatus$.next(false);
      this.scheduleReconnect();
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      this.connectionStatus$.next(false);
      this.scheduleReconnect();
    });

    // this.emit('ping', { ts: Date.now(), echo: 'testData' });
  }

  private scheduleReconnect(): void {
    this.reconnectAttempts++;
    const delay = Math.min(
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts),
      this.maxReconnectDelay,
    );

    console.log(`Reconnecting in ${delay / 1000}s...`);

    setTimeout(() => {
      this.connect();
    }, delay);
  }

  emit(event: string, data: any): void {
    if (this.socket?.connected) {
      this.socket.emit(event, data);
    } else {
      console.warn(`Cannot emit "${event}" — socket not connected`);
    }
  }

  emitRequestMessage(event: CommandType, data: CommandData) {
    this.emit(event, data);
  }

  emitRequestMessageLog(event: CommandType, data: CommandLogData[]) {
    this.emit(event, data);
  }

  listen<T>(event: string): Observable<T> {
    return new Observable<T>((subscriber) => {
      this.socket.on(event, (data: T) => {
        subscriber.next(data);
      });
    });
  }

  getConnectionStatus(): Observable<boolean> {
    return this.connectionStatus$.asObservable();
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
    }
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}
